import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog as MuiDialog,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

const CALLS = [
  {
    id: "claim-promo",
    title: "Claim a promo code",
    method: "POST",
    path: "/api/v1/partners/promotions/claim",
  },
  {
    id: "campaign",
    title: "Read campaign",
    method: "GET",
    path: "/api/v1/partners/promotions/campaign",
  },
  {
    id: "redeemed",
    title: "List redeemed codes",
    method: "GET",
    path: "/api/v1/partners/promotions/redeemed",
  },
];

const partnerUrl = (path, query) => {
  const base = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    const trimmed = String(value || "").trim();
    if (trimmed) params.set(key, trimmed);
  });
  const search = params.toString();
  return search ? `${base}${path}?${search}` : `${base}${path}`;
};

const buildHeaders = ({ apiKey, requestId, json }) => {
  const headers = {};
  if (json) headers["Content-Type"] = "application/json";
  const key = apiKey.trim();
  if (key) headers["X-Partner-Key"] = key;
  const id = requestId.trim();
  if (id) headers["X-Request-Id"] = id;
  return headers;
};

const buildClaimRequest = ({ apiKey, reference, requestId }) => {
  const body = {};
  const ref = reference.trim();
  if (ref) body.external_reference_id = ref;
  return {
    method: "POST",
    url: partnerUrl("/partners/promotions/claim"),
    headers: buildHeaders({ apiKey, requestId, json: true }),
    body,
  };
};

const buildCampaignRequest = ({ apiKey, requestId }) => ({
  method: "GET",
  url: partnerUrl("/partners/promotions/campaign"),
  headers: buildHeaders({ apiKey, requestId }),
});

const buildRedeemedRequest = ({ apiKey, requestId, redeemedFrom, redeemedTo }) => ({
  method: "GET",
  url: partnerUrl("/partners/promotions/redeemed", {
    redeemed_from: redeemedFrom,
    redeemed_to: redeemedTo,
  }),
  headers: buildHeaders({ apiKey, requestId }),
});

const sendPartnerRequest = async (request) => {
  const config = {
    headers: request.headers,
    validateStatus: () => true,
    timeout: 30000,
  };
  try {
    const response =
      request.method === "GET"
        ? await axios.get(request.url, config)
        : await axios.post(request.url, request.body, config);
    return { request, status: response.status, body: response.data };
  } catch (error) {
    const unreachable = error?.message === "Network Error";
    return {
      request,
      status: null,
      body: {
        message: unreachable
          ? "The browser could not reach the API. Check that the server is running."
          : error?.message || "The request did not reach the server.",
      },
    };
  }
};

const pretty = (value) => JSON.stringify(value, null, 2);

function CodeBlock({ title, text, empty, note }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 0.75 }}>
        <Typography variant='body2' fontWeight={700}>
          {title}
        </Typography>
        <Tooltip title={copied ? "Copied" : "Copy"}>
          <span>
            <IconButton aria-label={`Copy ${title}`} size='small' disabled={!text} onClick={copy}>
              {copied ? (
                <CheckRoundedIcon fontSize='small' color='success' />
              ) : (
                <ContentCopyRoundedIcon fontSize='small' />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Box
        component='pre'
        sx={{
          m: 0,
          p: 1.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.default",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          lineHeight: 1.5,
          overflow: "auto",
          maxHeight: 240,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text || empty}
      </Box>
      {note && (
        <Typography variant='caption' color='text.secondary' sx={{ display: "block", mt: 0.75 }}>
          {note}
        </Typography>
      )}
    </Box>
  );
}

function usePartnerCall(partner, issuedKey) {
  const rememberedKey =
    issuedKey?.partner_name === partner.partner_name ? issuedKey.api_key || "" : "";
  const [apiKey, setApiKey] = useState(rememberedKey);
  const [showKey, setShowKey] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async (request) => {
    setSending(true);
    try {
      setResult(await sendPartnerRequest(request));
    } finally {
      setSending(false);
    }
  };

  return {
    rememberedKey,
    apiKey,
    setApiKey,
    showKey,
    setShowKey,
    requestId,
    setRequestId,
    sending,
    result,
    send,
  };
}

function PartnerKeyFields({ call, partner }) {
  return (
    <>
      <TextField
        label='Partner key'
        type={call.showKey ? "text" : "password"}
        value={call.apiKey}
        disabled={call.sending}
        fullWidth
        onChange={(event) => call.setApiKey(event.target.value)}
        helperText={
          call.rememberedKey && call.apiKey === call.rememberedKey
            ? "Filled from the key you just saved. It only stays in this browser session."
            : "Paste the full key. We only keep the last four characters."
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton
                aria-label={call.showKey ? "Hide partner key" : "Show partner key"}
                edge='end'
                onClick={() => call.setShowKey((current) => !current)}
              >
                {call.showKey ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        label='Request id'
        value={call.requestId}
        disabled={call.sending}
        fullWidth
        onChange={(event) => call.setRequestId(event.target.value)}
        helperText='Optional. It comes back in the response.'
      />
      {!partner.has_api_key && (
        <Alert severity='warning' variant='outlined'>
          This partner has no key yet, so the call will be rejected until you add one.
        </Alert>
      )}
    </>
  );
}

function RequestPanels({ draft, result }) {
  const requestChanged = Boolean(result && pretty(result.request) !== pretty(draft));
  return (
    <>
      <Box>
        <Button
          variant='contained'
          disabled={draft.sending}
          onClick={draft.onSend}
          sx={{ boxShadow: "none" }}
          startIcon={draft.sending ? <CircularProgress size={16} color='inherit' /> : null}
        >
          {draft.sending ? "Sending..." : "Send request"}
        </Button>
      </Box>
      <CodeBlock title='Request' text={pretty(draft.request)} />
      <CodeBlock
        title='Response'
        text={
          result
            ? pretty({
                status: result.status,
                body: result.body,
              })
            : ""
        }
        empty='Send the request to see the response here.'
        note={requestChanged ? "You changed the request. Send it again to update the response." : ""}
      />
    </>
  );
}

function ClaimTester({ partner, issuedKey }) {
  const call = usePartnerCall(partner, issuedKey);
  const [reference, setReference] = useState("");
  const request = buildClaimRequest({
    apiKey: call.apiKey,
    reference,
    requestId: call.requestId,
  });

  return (
    <Stack spacing={2}>
      <PartnerKeyFields call={call} partner={partner} />
      <TextField
        label='Reference'
        value={reference}
        disabled={call.sending}
        fullWidth
        onChange={(event) => setReference(event.target.value)}
        helperText='Optional. Send the same reference again to get the same active code back.'
      />
      <RequestPanels
        result={call.result}
        draft={{
          sending: call.sending,
          request,
          onSend: () => call.send(request),
        }}
      />
    </Stack>
  );
}

function CampaignTester({ partner, issuedKey }) {
  const call = usePartnerCall(partner, issuedKey);
  const request = buildCampaignRequest({
    apiKey: call.apiKey,
    requestId: call.requestId,
  });

  return (
    <Stack spacing={2}>
      <PartnerKeyFields call={call} partner={partner} />
      <RequestPanels
        result={call.result}
        draft={{
          sending: call.sending,
          request,
          onSend: () => call.send(request),
        }}
      />
    </Stack>
  );
}

function RedeemedTester({ partner, issuedKey }) {
  const call = usePartnerCall(partner, issuedKey);
  const [redeemedFrom, setRedeemedFrom] = useState("");
  const [redeemedTo, setRedeemedTo] = useState("");
  const request = buildRedeemedRequest({
    apiKey: call.apiKey,
    requestId: call.requestId,
    redeemedFrom,
    redeemedTo,
  });

  return (
    <Stack spacing={2}>
      <PartnerKeyFields call={call} partner={partner} />
      <TextField
        label='Redeemed from'
        value={redeemedFrom}
        disabled={call.sending}
        fullWidth
        placeholder='2026-01-01'
        onChange={(event) => setRedeemedFrom(event.target.value)}
        helperText='Optional. A date, or a full time. Leave both dates empty to get every redeemed code.'
      />
      <TextField
        label='Redeemed to'
        value={redeemedTo}
        disabled={call.sending}
        fullWidth
        placeholder='2026-01-31'
        onChange={(event) => setRedeemedTo(event.target.value)}
        helperText='Optional. A date covers that whole UTC day.'
      />
      <RequestPanels
        result={call.result}
        draft={{
          sending: call.sending,
          request,
          onSend: () => call.send(request),
        }}
      />
    </Stack>
  );
}

export default function PartnerDevDialog({ partner, issuedKey, onClose }) {
  const [callId, setCallId] = useState(null);

  useEffect(() => {
    setCallId(null);
  }, [partner?.partner_name]);

  return (
    <MuiDialog
      open={Boolean(partner)}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      aria-labelledby='partner-dev-title'
    >
      <DialogTitle id='partner-dev-title' sx={{ fontWeight: 750 }}>
        Dev tools
        {partner?.partner_name ? ` · ${partner.partner_name}` : ""}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Alert severity='warning' variant='outlined'>
            For testing. These call the live API. Claiming a promo code creates a real code. The
            other two calls only read data.
          </Alert>
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
            <List disablePadding>
              {CALLS.map((call) => (
                <ListItemButton
                  key={call.id}
                  selected={callId === call.id}
                  onClick={() => setCallId(call.id)}
                >
                  <ListItemText
                    primary={call.title}
                    secondary={`${call.method} ${call.path}`}
                    primaryTypographyProps={{ fontWeight: 650, variant: "body2" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                  <Chip size='small' label={call.method} sx={{ fontWeight: 650 }} />
                </ListItemButton>
              ))}
            </List>
          </Box>
          {partner && callId === "claim-promo" && (
            <ClaimTester
              key={`claim:${partner.partner_name}:${issuedKey?.api_key || ""}`}
              partner={partner}
              issuedKey={issuedKey}
            />
          )}
          {partner && callId === "campaign" && (
            <CampaignTester
              key={`campaign:${partner.partner_name}:${issuedKey?.api_key || ""}`}
              partner={partner}
              issuedKey={issuedKey}
            />
          )}
          {partner && callId === "redeemed" && (
            <RedeemedTester
              key={`redeemed:${partner.partner_name}:${issuedKey?.api_key || ""}`}
              partner={partner}
              issuedKey={issuedKey}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color='inherit' onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </MuiDialog>
  );
}
